import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useOrganization} from 'crqlar-react-common/dist/state/auth.reducer';
import {formatDateHM, formatFullDMY} from 'crqlar-react-common/dist/utils/date.util';

import {scheduleTimeZone} from '../../../utils/timeZone';
import NodeEditorLayout from '../NodeEditorLayout';
import NodeDateTimeField from '../fields/NodeDateTimeField';
import {MultiSelect, SingleSelect} from '../../../../../common/newSelect';
import type {SelectOption} from '../../../../../common/newSelect';
import {NodeEditorV2Props} from '../types';

const DAYS_OF_WEEK = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const DAY_LABEL_KEYS: Record<string, string> = {
    MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun'
};

// The summary's full weekday ("jeden Freitag", not "jeden Fr"), in the UI language; 1 Jan 2024 was a Monday.
const weekdayName = (day: string, locale: string) =>
    new Intl.DateTimeFormat(locale, {weekday: 'long'}).format(new Date(2024, 0, 1 + DAYS_OF_WEEK.indexOf(day)));

// No weekday picked runs on the start date's (the API's ScheduleRules.latestWeeklySlot). Local like the time shown:
// the API's UTC weekday at the UTC time is this weekday at this local time.
const startWeekday = (startDate: Date) => DAYS_OF_WEEK[(startDate.getDay() + 6) % 7];

// What a missed slot gets, as the API's ScheduleRules.dueTriggerPoint decides it.
const CATCH_UP_KEYS: Record<string, string> = {
    once: 'A run missed while the system is down happens as soon as it is back.',
    daily: 'A run missed while the system is down happens later that day, once it is back.',
    weekly: 'A run missed while the system is down happens once it is back, unless the next one is due by then.',
    monthly: 'A run missed while the system is down happens later that month, once it is back.',
};

const defaultStartDate = (): Date => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);
    return tomorrow;
};

const ScheduledTriggerEditorV2 = ({nodeConfig, setNodeConfig, registerBeforeSave, readOnly}: NodeEditorV2Props) => {
    const {t, i18n} = useTranslation();
    const zone = scheduleTimeZone(useOrganization());

    const scheduleType: string = nodeConfig.scheduleType || 'once';
    const selectedDays: string[] = nodeConfig.daysOfWeek || [];
    const parsedDate = nodeConfig.startDate ? new Date(nodeConfig.startDate) : null;
    const startDate = parsedDate && !isNaN(parsedDate.getTime()) ? parsedDate : undefined;
    const hasStartDate = !!startDate;
    // '' is what the × on Starts writes: the user emptied the field, so it stays empty and the trigger never fires.
    const cleared = nodeConfig.startDate === '';

    // Only a node that never stored a start gets one seeded. A stored date comes back from the API as a `Date`
    // (common `api.ts`) and counts as stored; an absent `scheduleType` is "once" on both sides.
    useEffect(() => {
        if (readOnly || hasStartDate || cleared) return;
        setNodeConfig({...nodeConfig, scheduleType, startDate: defaultStartDate().toISOString()});
    }, [readOnly, nodeConfig, hasStartDate, cleared, scheduleType, setNodeConfig]);

    // Every save writes the organization's zone: with it the API keeps the time of day across daylight saving
    // (`ScheduleRules`), a node without one runs on the UTC calendar.
    useEffect(() => {
        registerBeforeSave?.(async () => (zone ? {config: {...nodeConfig, zone}} : true));
        return () => registerBeforeSave?.(null);
    }, [registerBeforeSave, nodeConfig, zone]);

    const repeatOptions: SelectOption[] = [
        {value: 'once', label: t('Once')},
        {value: 'daily', label: t('Every day')},
        {value: 'weekly', label: t('Every week')},
        {value: 'monthly', label: t('Every month')},
    ];

    const dayOfMonthOptions: SelectOption[] = [
        {value: '', label: t('Day of the start date ({{day}})', {day: startDate ? startDate.getDate() : '—'})},
        ...Array.from({length: 31}, (_, index) => ({
            value: String(index + 1),
            label: String(index + 1),
        })),
    ];

    // The Starts field's own format, so the summary reads the same date and time.
    const time = startDate ? formatDateHM(startDate) : '';
    const date = startDate ? formatFullDMY(startDate) : '';

    const handleRepeatChange = (type: string) => setNodeConfig({
        ...nodeConfig,
        scheduleType: type,
        daysOfWeek: type === 'weekly' ? selectedDays : undefined,
        dayOfMonth: type === 'monthly' ? nodeConfig.dayOfMonth : undefined,
    });

    const handleDaysChange = (days: string[]) => setNodeConfig({...nodeConfig, daysOfWeek: days});

    // The select keeps the order it is handed, so the days read in the week's own order.
    const dayOptions: SelectOption[] = DAYS_OF_WEEK.map(day => ({
        value: day,
        label: t(DAY_LABEL_KEYS[day])
    }));

    const summary = () => {
        // The card's "No schedule configured", and why it matters.
        if (!startDate) return t('No schedule configured. Pick when it starts — until then this trigger never fires.');

        const catchUp = t(CATCH_UP_KEYS[scheduleType] ?? CATCH_UP_KEYS.once);

        switch (scheduleType) {
            case 'daily':
                return `${t('Runs every day at {{time}}, starting {{date}}.', {time, date})} ${catchUp}`;
            case 'weekly': {
                const picked = DAYS_OF_WEEK.filter(day => selectedDays.includes(day));
                const days = (picked.length ? picked : [startWeekday(startDate)])
                    .map(day => weekdayName(day, i18n.resolvedLanguage || i18n.language || 'en'))
                    .join(', ');
                return `${t('Runs every {{days}} at {{time}}, starting {{date}}.', {days, time, date})} ${catchUp}`;
            }
            case 'monthly': {
                const day = nodeConfig.dayOfMonth || startDate.getDate();
                return `${t('Runs on day {{day}} of every month at {{time}}, starting {{date}}.', {day, time, date})} ${catchUp}`;
            }
            default:
                return `${t('Runs once, on {{date}} at {{time}}.', {date, time})} ${catchUp}`;
        }
    };

    return (
        <NodeEditorLayout
            state={startDate ? 'configured' : 'wontRun'}
            summary={summary()}
        >
            <SingleSelect
                label={t('Repeats')}
                value={scheduleType}
                options={repeatOptions}
                onChange={handleRepeatChange}
                disabled={readOnly}
                disableClearable
            />

            <NodeDateTimeField
                label={t('Starts')}
                value={startDate}
                onChange={value => setNodeConfig({
                    ...nodeConfig,
                    startDate: value ? value.toISOString() : '',
                })}
                disabled={readOnly}
            />

            {scheduleType === 'weekly' && (
                <MultiSelect
                    label={t('On these days')}
                    placeholder={t('Day of the start date ({{day}})', {
                        day: startDate ? t(DAY_LABEL_KEYS[startWeekday(startDate)]) : '—'
                    })}
                    value={selectedDays}
                    options={dayOptions}
                    onChange={handleDaysChange}
                    disabled={readOnly}
                />
            )}

            {scheduleType === 'monthly' && (
                <SingleSelect
                    label={t('Day of month')}
                    value={nodeConfig.dayOfMonth ? String(nodeConfig.dayOfMonth) : ''}
                    options={dayOfMonthOptions}
                    onChange={day => setNodeConfig({
                        ...nodeConfig,
                        dayOfMonth: day ? Number(day) : undefined,
                    })}
                    disabled={readOnly}
                />
            )}
        </NodeEditorLayout>
    );
};

export default ScheduledTriggerEditorV2;
